import React, {
  useState,
  memo,
  useEffect,
  useRef,
  useImperativeHandle,
} from 'react';
import { Form, Input } from 'antd';
import { DragableTableFrom } from '@components';
import { columns } from './configs/columns.config';
import { FORM_KEYS } from './configs/form.keys';

import actions from '@flux/action';
import RichEditor from '@modules/questionnaires/complexity/richEditor';
import { customDeleteConfirm } from '@components/dragableTableFrom/formItems';

const WRAPPER_KEY = FORM_KEYS.TABLE_WRAPPER.key;

function GuidingPrinciples({
  guidingPrincipleTemplateInfo = [],
  discussionGuideTemplateInfo = [],
  cRef,
  companyInstance = 'Buy-Side',
  formChange = () => {},
}) {
  // console.log({ guidingPrincipleTemplateInfo });
  const [refreshCount, setRefreshCount] = useState(0); // for rich editor
  const handleAddDiscussion = useRef(null);
  const [form] = Form.useForm();

  const fieldMethods = useRef({});
  const registerFieldsMethods = (methods) => {
    fieldMethods.current = methods;
  };

  const handleSortEnd = ({ newIndex, oldIndex }) => {
    const { move } = fieldMethods.current;

    if (move) {
      move(oldIndex, newIndex);
      formChange();
    }
  };

  useImperativeHandle(cRef, () => ({
    getForm: () => form,
  }));

  useEffect(() => {
    if (
      !Array.isArray(guidingPrincipleTemplateInfo) ||
      (guidingPrincipleTemplateInfo.length === 0 &&
        form.getFieldValue(WRAPPER_KEY)?.length === 0)
    ) {
      return;
    }
    form.setFieldsValue({ [WRAPPER_KEY]: guidingPrincipleTemplateInfo });
    if (companyInstance !== '') {
      setRefreshCount((v) => v + 1);
    }
  }, [guidingPrincipleTemplateInfo]);

  useEffect(() => {
    if (
      !Array.isArray(discussionGuideTemplateInfo) ||
      (discussionGuideTemplateInfo.length === 0 &&
        form.getFieldValue('discussionGuideList')?.length === 0)
    ) {
      return;
    }
    form.setFieldsValue({ discussionGuideList: discussionGuideTemplateInfo });
    if (companyInstance !== '') {
      setRefreshCount((v) => v + 1);
    }
  }, [discussionGuideTemplateInfo]);

  const addDiscussion = () => {
    typeof handleAddDiscussion.current === 'function' &&
      handleAddDiscussion.current();
    formChange();
    setTimeout(() => {
      const discussionItemNodes = document.querySelectorAll('.discussion-item');
      if (discussionItemNodes && discussionItemNodes?.length > 0) {
        discussionItemNodes[discussionItemNodes.length - 1].scrollIntoView({
          behavior: 'smooth',
        });
      }
    }, 200);
  };

  const removeDiscussion = (index, remove, name) => {
    const title = (form.getFieldValue(`discussionGuideList`) || [])[index]
      ?.title;
    customDeleteConfirm({
      customContent: `Do you confirm to delete ${title || 'this item'}?`,
      customOK: 'ok',
      onOk() {
        remove && remove(name);
        formChange();
      },
    });
  };
  return (
    <div className="manage-module-wrapper system-settings-guiding-principle-wrapper with-discussion-point guide-wrapper">
      <Form
        form={form}
        scrollToFirstError
        autoComplete="off"
        className="integration-manage-form"
      >
        <div className="dragable-table-form">
          <DragableTableFrom
            registerFieldsMethods={registerFieldsMethods}
            onSortEnd={handleSortEnd}
            isActive
            formInstance={form}
            columns={columns}
            name={WRAPPER_KEY}
            isLoading={false}
            addBtnText="Add New Guiding Principle"
            refreshCount={refreshCount}
            formChange={formChange}
            wrapperContainerClass=".system-settings-guiding-principle-wrapper"
          />
        </div>
        <div className="discussion-point-wrapper">
          <div className="collapse-header">
            <span>{`${companyInstance} Guiding Principles`}</span>
          </div>

          <div className="collapse-purpose">
            <i>Purpose:</i>{' '}
            {`Discussion guide for key executives to assist in
            converting acquisition strategy into ${companyInstance.toLowerCase()} strategy. The
            output of this document should be shared with the ${companyInstance}
            Organization to guide ${companyInstance.toLowerCase()} planning and execution.`}
            <div className="discussion-btn-wrapper">
              <b>Points of Discussion:</b>
              <span className="add-discussion-btn" onClick={addDiscussion}>
                <span className="appkiticon icon-circle-plus-fill a-font-16" />
                Add New Discussion Point
              </span>
            </div>
          </div>

          <div className="discussion-divider" />
          <div className="discussion-list">
            <Form.List name="discussionGuideList">
              {(fields, { add, remove }) => {
                handleAddDiscussion.current = add;
                return fields.map(({ key, name, ...restField }, index) => (
                  <div className="discussion-item" key={key}>
                    <div className="discussion-item-header">
                      <Form.Item
                        name={[name, 'title']}
                        rules={[
                          {
                            required: true,
                            message: 'Please input discussion title!',
                          },
                        ]}
                      >
                        <Input
                          className="discussion-item-title"
                          maxLength={60}
                          onChange={formChange}
                        />
                      </Form.Item>
                      <span
                        className="appkiticon icon-delete-fill a-font-14"
                        onClick={() => {
                          removeDiscussion(index, remove, name);
                        }}
                      />
                    </div>
                    <Form.Item
                      name={[name, 'discussionPoint']}
                      className="discussion-item-editor"
                      // initialValue={}
                    >
                      <RichEditor
                        key={`${key}-${refreshCount}`}
                        formChange={formChange}
                      />
                    </Form.Item>
                  </div>
                ));
              }}
            </Form.List>
          </div>
        </div>
      </Form>
    </div>
  );
}

export default memo(GuidingPrinciples);
